import { VaultService } from '@api'
import { attach, createEffect, createEvent, createStore } from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import { handleError } from '@/utils/error'

export const getVaultCategorySortFx = attach({
  name: 'get vault category sort',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (_: void, { creatorId }) => ({
    creatorId: creatorId!,
  }),
  effect: createEffect(VaultService.getVaultSorting),
})

getVaultCategorySortFx.fail.watch(({ error, params }) => {
  handleError(
    error,
    'Something went wrong while getting vault category sort',
    () => getVaultCategorySortFx(params),
  )
})

export const setVaultCategorySortFx = attach({
  name: 'set vault category sort',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (params: { sort: string; order: string }, { creatorId }) => ({
    ...params,
    creatorId: creatorId!,
  }),
  effect: createEffect(
    async ({
      creatorId,
      sort,
      order,
    }: {
      creatorId: number
      sort: string
      order: string
    }) => {
      await VaultService.sortCategory({
        creatorId,
        requestBody: { sort, order },
      })

      return { sort, order }
    },
  ),
})

setVaultCategorySortFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while setting vault category sort')
})

export const setVaultCategoryCustomOrderFx = attach({
  name: 'set vault category custom order',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (customOrder: number[], { creatorId }) => ({
    creatorId: creatorId!,
    requestBody: { customOrder },
  }),
  effect: createEffect(VaultService.sortCategoryCustom),
})

setVaultCategoryCustomOrderFx.doneData.watch(() => {
  toast.success('Custom category order has been set successfully')
})

setVaultCategoryCustomOrderFx.failData.watch((error) => {
  handleError(error, 'Error setting custom category order')
})

export const $categorySort = createStore('default')
  .on(
    [getVaultCategorySortFx.doneData, setVaultCategorySortFx.doneData],
    (_, { sort }) => sort,
  )
  .reset(setVaultCategoryCustomOrderFx.doneData)

export const $categoryOrder = createStore('asc')
  .on(
    [getVaultCategorySortFx.doneData, setVaultCategorySortFx.doneData],
    (_, { order }) => order,
  )
  .reset(setVaultCategoryCustomOrderFx.doneData)

export const setIsCustomCategory = createEvent<boolean>(
  'set vault category is custom',
)
export const $isCustomCategory = createStore(false, {
  name: 'is vault category custom',
})
  .on(setIsCustomCategory, (_, value) => value)
  .reset(setVaultCategoryCustomOrderFx.doneData)
